import type { MeResponse } from "@safeplate/contracts";

import { requestApi } from "../api/client.js";

export interface SharedDeviceProfile {
  display_name: string;
  image: string | null;
  roles: Array<"admin" | "manager" | "staff">;
  staff_profile_id: string;
}

export interface SharedDeviceRoster {
  device_id: string;
  org_id: string;
  org_name: string;
  profiles: SharedDeviceProfile[];
}

let volatileInstallationId: string | null = null;

export function browserInstallationId(): string {
  const key = "safeplate_browser_installation";
  try {
    const stored = globalThis.localStorage.getItem(key);
    if (stored) return stored;
    const created = crypto.randomUUID();
    globalThis.localStorage.setItem(key, created);
    return created;
  } catch {
    volatileInstallationId ??= crypto.randomUUID();
    return volatileInstallationId;
  }
}

export function bindSharedDevice(orgId: string): Promise<void> {
  return requestApi("/shared-devices", {
    body: JSON.stringify({
      org_id: orgId,
      installation_id: browserInstallationId(),
      platform: "web",
      display_name: "Shared browser",
    }),
    headers: { "X-Org-Id": orgId },
    method: "POST",
  });
}

export function getSharedDeviceRoster(): Promise<SharedDeviceRoster> {
  return requestApi<SharedDeviceRoster>("/shared-device/roster");
}

export function enrollCurrentDeviceAccount(): Promise<void> {
  return requestApi("/shared-device/accounts", { method: "POST" });
}

export function removeCurrentDeviceAccount(): Promise<void> {
  return requestApi("/shared-device/accounts/me", { method: "DELETE" });
}

export function unlockSharedDevice(
  staffProfileId: string,
  pin: string,
): Promise<void> {
  return requestApi("/shared-device/unlock", {
    body: JSON.stringify({ staff_profile_id: staffProfileId, pin }),
    method: "POST",
  });
}

export function lockSharedDevice(): Promise<void> {
  return requestApi("/shared-device/lock", { method: "POST" });
}

export function unbindSharedDevice(): Promise<void> {
  return requestApi("/shared-device", { method: "DELETE" });
}

export function setStaffQuickPin(
  staffProfileId: string,
  pin: string,
): Promise<void> {
  return requestApi(`/staff/${staffProfileId}/quick-pin`, {
    body: JSON.stringify({ pin }),
    method: "PUT",
  });
}

export function scopeMeToOrganisation(
  me: MeResponse,
  orgId: string,
): MeResponse {
  return {
    ...me,
    memberships: me.memberships.filter(
      (membership) => membership.org_id === orgId,
    ),
    staff_profiles: me.staff_profiles.filter(
      (profile) => profile.org_id === orgId,
    ),
  };
}
